import React from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background font-sans antialiased">
      <Navbar />
      
      <main className="pt-24 pb-20">
        {/* Header */}
        <div className="container mx-auto px-6 mb-12">
          <div className="mb-8">
            <Button variant="ghost" asChild className="gap-2 pl-0 hover:pl-2 transition-all">
              <Link to="/">
                <ArrowLeft className="h-4 w-4" /> Back to Home
              </Link>
            </Button>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Privacy <span className="text-secondary">Policy</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            At WorkRidePro, we are committed to protecting your personal data and ensuring your commute is not just efficient, but also secure.
          </p>
          <p className="text-sm text-muted-foreground mt-4">
            Last Updated: November 28, 2024
          </p>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 grid lg:grid-cols-3 gap-12">
          
          {/* Main Text */}
          <div className="lg:col-span-2 space-y-12">
            
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">1. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed">
                We collect information to provide better transport services to you and your organization. This includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Personal Identification:</strong> Name, employee ID, email address, and phone number.</li>
                <li><strong>Location Data:</strong> Real-time GPS location during your scheduled trips for routing and safety monitoring.</li>
                <li><strong>Usage Data:</strong> Trip history, pickup/drop-off preferences, and feedback ratings.</li>
                <li><strong>Device Information:</strong> Device type, operating system, and app version for troubleshooting.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">2. How We Use Your Data</h2>
              <p className="text-muted-foreground leading-relaxed">
                Your data is used strictly for operational purposes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Route Optimization:</strong> AI algorithms use location data to create the most efficient routes.</li>
                <li><strong>Safety Monitoring:</strong> Live tracking allows our Command Center to monitor trips and respond to SOS alerts.</li>
                <li><strong>Communication:</strong> Sending trip updates, ETA notifications, and driver details.</li>
                <li><strong>Compliance:</strong> Generating reports for your employer regarding transport usage and billing.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">3. Data Sharing & Disclosure</h2>
              <p className="text-muted-foreground leading-relaxed">
                We do not sell your personal data. We share data only in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>With Your Employer:</strong> To verify trip completion and ensure employee safety.</li>
                <li><strong>With Service Providers:</strong> Cloud hosting (AWS/Azure) and map services (Google Maps) strictly for app functionality.</li>
                <li><strong>Legal Requirements:</strong> If required by law enforcement or to protect the safety of our users.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">4. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We employ industry-standard security measures:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256).</li>
                <li>Strict access controls and regular security audits.</li>
                <li>Anonymization of location data where possible for analytics.</li>
              </ul>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-primary">5. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed">
                Depending on your region, you may have the right to access, correct, or delete your personal data. Please contact your organization's transport administrator or email us at <a href="mailto:privacy@workridepro.com" className="text-secondary hover:underline">privacy@workridepro.com</a> for assistance.
              </p>
            </section>

          </div>

          {/* Sidebar / Highlights */}
          <div className="lg:col-span-1">
            <div className="bg-slate-50 dark:bg-slate-900 p-8 rounded-2xl border border-border sticky top-24">
              <h3 className="text-xl font-bold text-primary mb-6">Key Highlights</h3>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Encrypted Data</h4>
                    <p className="text-sm text-muted-foreground">All your personal info is encrypted end-to-end.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">No Data Selling</h4>
                    <p className="text-sm text-muted-foreground">We never sell your location or personal data to advertisers.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Eye className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground">Transparency</h4>
                    <p className="text-sm text-muted-foreground">You always know what data is being collected and why.</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-8 border-t border-border">
                <p className="text-sm font-medium text-foreground mb-2">Have questions?</p>
                <a href="mailto:privacy@workridepro.com" className="text-secondary font-bold hover:underline">
                  Contact Privacy Team
                </a>
              </div>
            </div>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}
